import { AxiosResponse } from 'axios';
import { AbortControllerManager } from '../abortControllerManager';
import { authClient, kioskClient } from '../client';
import type {
  TGetAppointmentsParams,
  ICreateAppointmentRequest,
  ICreateAppointmentResponse,
  IPatchAppointmentRequest,
  IGetAppointmentsResponse,
  IGetOneAppointmentResponse,
  IPutAppointmentParticipant,
  IPostAppointmentFeedbackRequest,
  TNullable,
  TAppointmentKioskPolling,
  ITemplateResponse,
  IUpdateOutreachStatusRequest,
  IAddOutreachNoteRequest,
  ICreateVisitNoteRequest,
  IAddEvidenceRequest,
  ICreateCallRequestRequest,
  ICallRequestResponse,
  IListCallRequestsResponse,
} from '@/types';

class AppointmentsApi extends AbortControllerManager {
  private readonly APPOINTMENTS_URL = '/v2/appointments';
  private readonly CALL_REQUESTS_URL = '/v2/call-requests';

  constructor() {
    super();
  }

  createAppointment(
    body: Partial<ICreateAppointmentRequest>,
  ): Promise<ICreateAppointmentResponse> {
    const signal = this.createAbortController('createAppointment');
    return authClient.post(this.APPOINTMENTS_URL, body, { signal });
  }

  getAppointments(
    params: TGetAppointmentsParams,
  ): Promise<IGetAppointmentsResponse> {
    const signal = this.createAbortController('getAppointments');
    return authClient.get(this.APPOINTMENTS_URL, { params, signal });
  }

  getAppointmentsKiosk(): Promise<
    ITemplateResponse<TAppointmentKioskPolling[]>
  > {
    const signal = this.createAbortController('getAppointmentsByKiosk');
    return kioskClient.get(this.APPOINTMENTS_URL, { signal });
  }

  getAppointment(id: string): Promise<IGetOneAppointmentResponse> {
    const signal = this.createAbortController(`getAppointment-${id}`);
    return authClient.get(`${this.APPOINTMENTS_URL}/${id}`, { signal });
  }

  sendAppointmentEvent(
    appointmentId: string,
    body: IPostAppointmentFeedbackRequest,
  ): Promise<void> {
    const signal = this.createAbortController(
      `sendAppointmentEvent-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/event`,
      body,
      { signal },
    );
  }

  getAppointmentsAll(
    params: Pick<
      TGetAppointmentsParams,
      'institution_id' | 'page' | 'per_page'
    >,
  ): Promise<IGetAppointmentsResponse> {
    const signal = this.createAbortController('getAppointmentsAll');
    return authClient.get(`${this.APPOINTMENTS_URL}/all`, { params, signal });
  }

  patchAppointment(appointmentId: string, body: IPatchAppointmentRequest) {
    const signal = this.createAbortController(
      `patchAppointment-${appointmentId}`,
    );
    return authClient.patch(`${this.APPOINTMENTS_URL}/${appointmentId}`, body, {
      signal,
    });
  }

  addVisitNote(
    appointmentId: string,
    body: ICreateVisitNoteRequest,
  ): Promise<IGetOneAppointmentResponse> {
    const signal = this.createAbortController(`addVisitNote-${appointmentId}`);
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/visit-notes`,
      body,
      { signal },
    );
  }

  addEvidence(
    appointmentId: string,
    body: IAddEvidenceRequest,
  ): Promise<IGetOneAppointmentResponse> {
    const signal = this.createAbortController(
      `addEvidence-${appointmentId}-${body.document_id}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/evidence`,
      body,
      { signal },
    );
  }

  removeEvidence(
    appointmentId: string,
    documentId: string,
  ): Promise<IGetOneAppointmentResponse> {
    const signal = this.createAbortController(
      `removeEvidence-${appointmentId}-${documentId}`,
    );
    return authClient.delete(
      `${this.APPOINTMENTS_URL}/${appointmentId}/evidence/${documentId}`,
      { signal },
    );
  }

  exportPdf(appointmentId: string): Promise<AxiosResponse<Blob>> {
    const signal = this.createAbortController(`exportPdf-${appointmentId}`);
    return authClient.get(`${this.APPOINTMENTS_URL}/${appointmentId}/pdf`, {
      responseType: 'blob',
      signal,
    });
  }

  rescheduleAppointment(
    appointmentId: string,
    body: { start: string; duration: number; kioskId?: TNullable<string> },
  ) {
    const signal = this.createAbortController(
      `rescheduleAppointment-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/reschedule`,
      body,
      { signal },
    );
  }

  cancelAppointment(appointmentId: string, reason?: string) {
    const signal = this.createAbortController(
      `cancelAppointment-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/cancel`,
      { reason },
      { signal },
    );
  }

  rescheduleDeclineAppointment(appointmentId: string) {
    const signal = this.createAbortController(
      `declineRescheduleAppointment-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/reschedule/decline`,
      undefined,
      { signal },
    );
  }

  confirmAppointment(appointmentId: string) {
    const signal = this.createAbortController(
      `confirmAppointment-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/confirm`,
      undefined,
      { signal },
    );
  }

  dispatcherAppointmentApproved(appointmentId: string) {
    const signal = this.createAbortController(
      `dispatcherAppointmentApproved-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/approve`,
      undefined,
      { signal },
    );
  }

  dispatcherAppointmentDeclined(appointmentId: string, reason?: string) {
    const signal = this.createAbortController(
      `dispatcherAppointmentDeclined-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/decline`,
      { reason },
      { signal },
    );
  }

  dispatcherAppointmentPay(appointmentId: string) {
    const signal = this.createAbortController(
      `dispatcherAppointmentPay-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/pay`,
      undefined,
      { signal },
    );
  }

  dispatcherAppointmentCopay(appointmentId: string) {
    const signal = this.createAbortController(
      `dispatcherAppointmentCopay-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/copay`,
      undefined,
      { signal },
    );
  }

  updateOutreachStatus(
    appointmentId: string,
    body: IUpdateOutreachStatusRequest,
  ): Promise<void> {
    const signal = this.createAbortController(
      `updateOutreachStatus-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/outreach-status`,
      body,
      { signal },
    );
  }

  addOutreachNote(
    appointmentId: string,
    body: IAddOutreachNoteRequest,
  ): Promise<void> {
    const signal = this.createAbortController(
      `addOutreachNote-${appointmentId}`,
    );
    return authClient.post(
      `${this.APPOINTMENTS_URL}/${appointmentId}/dispatcher/outreach-note`,
      body,
      { signal },
    );
  }

  addParticipant(appointmentId: string, body: IPutAppointmentParticipant) {
    const signal = this.createAbortController(
      `addParticipantInAppointment-${appointmentId}`,
    );
    return authClient.put(
      `${this.APPOINTMENTS_URL}/${appointmentId}/participants`,
      body,
      { signal },
    );
  }

  createCallRequest(
    body: ICreateCallRequestRequest,
  ): Promise<ICallRequestResponse> {
    const signal = this.createAbortController('createCallRequest');
    return authClient.post(this.CALL_REQUESTS_URL, body, { signal });
  }

  getPendingCallRequests(): Promise<IListCallRequestsResponse> {
    const signal = this.createAbortController('getPendingCallRequests');
    return authClient.get(this.CALL_REQUESTS_URL, { signal });
  }

  acceptCallRequest(id: string): Promise<ICallRequestResponse> {
    const signal = this.createAbortController(`acceptCallRequest-${id}`);
    return authClient.post(
      `${this.CALL_REQUESTS_URL}/${id}/accept`,
      undefined,
      {
        signal,
      },
    );
  }

  declineCallRequest(id: string): Promise<ICallRequestResponse> {
    const signal = this.createAbortController(`declineCallRequest-${id}`);
    return authClient.post(
      `${this.CALL_REQUESTS_URL}/${id}/decline`,
      undefined,
      { signal },
    );
  }
}

export const appointmentsApi = new AppointmentsApi();
